import React, { useState, useMemo, useRef } from 'react';
import { X, Send, Loader2, MessageCircle, Bold, Italic, Underline, List, Link2, Type } from 'lucide-react';
import axios from 'axios';

const EmailComposerModal = ({ 
  isOpen, 
  onClose, 
  customerEmail, 
  customerName, 
  orderData, 
  onEmailSent 
}) => {
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const textareaRef = useRef(null);
  
  // Message templates based on order data
  const messageTemplates = useMemo(() => {
    if (!orderData) return [];
    
    const orderId = orderData.id?.slice(0, 8).toUpperCase() || 'N/A';
    const planType = orderData.subscription_type?.charAt(0).toUpperCase() + 
                    (orderData.subscription_type?.slice(1) || 'N/A');
    
    return [
      {
        id: 'order_inquiry',
        name: 'Order Inquiry',
        subject: `Order ${orderId} - ${planType} Plan Inquiry`,
        body: `<p>Dear ${customerName},</p>

<p>Thank you for your recent order with Portfolio Auditor. I'm reaching out regarding your <strong>${planType}</strong> plan purchase.</p>

<p><strong>Order Details:</strong></p>
<ul>
<li><strong>Order ID:</strong> ${orderId}</li>
<li><strong>Subscription Type:</strong> ${planType} Plan</li>
<li><strong>Order Status:</strong> ${orderData.status?.charAt(0).toUpperCase() + orderData.status?.slice(1)}</li>
</ul>

<p>[Your custom message here]</p>`
      },
      {
        id: 'technical_support',
        name: 'Technical Support',
        subject: `Order ${orderId} - Technical Support`,
        body: `<p>Dear ${customerName},</p>

<p>I hope this message finds you well. I'm writing to assist you with any technical questions you may have regarding your <strong>${planType}</strong> plan software.</p>

<p><strong>Your Order Information:</strong></p>
<ul>
<li><strong>Order ID:</strong> ${orderId}</li>
<li><strong>Plan:</strong> ${planType}</li>
<li><strong>Status:</strong> ${orderData.status?.charAt(0).toUpperCase() + orderData.status?.slice(1)}</li>
</ul>

<p>[Your custom message here]</p>`
      },
      {
        id: 'download_assistance',
        name: 'Download Assistance',
        subject: `Order ${orderId} - Download Assistance`,
        body: `<p>Dear ${customerName},</p>

<p>I'm here to help you with downloading and installing your <strong>${planType}</strong> plan software.</p>

<p><strong>Order Information:</strong></p>
<ul>
<li><strong>Order ID:</strong> ${orderId}</li>
<li><strong>Subscription:</strong> ${planType} Plan</li>
<li><strong>Status:</strong> ${orderData.status?.charAt(0).toUpperCase() + orderData.status?.slice(1)}</li>
</ul>

<p>You can access your software download from your <a href="${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}/dashboard" target="_blank">Portfolio Auditor Dashboard</a>.</p>

<p>[Your custom message here]</p>`
      },
      {
        id: 'general_support',
        name: 'General Support',
        subject: `Portfolio Auditor Support - Order ${orderId}`,
        body: `<p>Dear ${customerName},</p>

<p>Thank you for choosing Portfolio Auditor. I'm reaching out to provide you with personalized support for your account.</p>

<p><strong>Your Account Details:</strong></p>
<ul>
<li><strong>Recent Order:</strong> ${orderId}</li>
<li><strong>Current Plan:</strong> ${planType}</li>
<li><strong>Order Status:</strong> ${orderData.status?.charAt(0).toUpperCase() + orderData.status?.slice(1)}</li>
</ul>

<p>[Your custom message here]</p>`
      }
    ];
  }, [orderData, customerName]);

  // Default footer for all emails
  const defaultFooter = `
<br><br>
<hr>
<p><strong>Best regards,</strong><br>
Portfolio Auditor Support Team<br>
<strong>Email:</strong> ${process.env.REACT_APP_COMPANY_EMAIL || 'support@portfolioauditor.com'}<br>
<strong>Website:</strong> <a href="${process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}" target="_blank">Portfolio Auditor</a></p>

<p><em>This email was sent from the Portfolio Auditor Admin Panel. Please contact us if you have any questions or need further assistance.</em></p>`;

  // Rich text formatting functions
  const insertFormatting = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    const newText = before + selectedText + after;
    
    const newMessage = message.substring(0, start) + newText + message.substring(end);
    setMessage(newMessage);
    
    // Reset cursor position
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const formatBold = () => insertFormatting('<strong>', '</strong>');
  const formatItalic = () => insertFormatting('<em>', '</em>');
  const formatUnderline = () => insertFormatting('<u>', '</u>');
  const formatHeading = () => insertFormatting('<h3>', '</h3>');
  const insertList = () => insertFormatting('<ul><li>', '</li></ul>');
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      insertFormatting(`<a href="${url}">`, '</a>');
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    const template = messageTemplates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setMessage(template.body);
      setSelectedTemplate(templateId);
    }
  };

  // Handle message change
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  // Send email
  const handleSendEmail = async () => {
    if (!subject.trim() || !message.trim()) {
      alert('Please fill in both subject and message.');
      return;
    }

    setLoading(true);
    try {
      const emailContent = message + defaultFooter;
      
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-custom-email`, {
        to_email: customerEmail,
        to_name: customerName,
        subject: subject,
        message: emailContent,
        order_id: orderData?.id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (onEmailSent) {
        onEmailSent('success', 'Email sent successfully!', `Your message has been sent to ${customerEmail}`);
      }
      
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      if (onEmailSent) {
        onEmailSent('error', 'Failed to send email', error.response?.data?.detail || 'Please try again later');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear form
  const handleClear = () => {
    setSubject('');
    setMessage('');
    setSelectedTemplate('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <MessageCircle className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Compose Email</h2>
              <p className="text-sm text-gray-600 mt-1">
                Send personalized email to {customerName} ({customerEmail})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Email Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <p className="text-sm text-gray-600">{process.env.REACT_APP_COMPANY_EMAIL || 'support@portfolioauditor.com'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <p className="text-sm text-gray-600">{customerEmail}</p>
              </div>
            </div>

            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message Templates</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {messageTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template.id)}
                    className={`p-3 text-sm rounded-lg border transition-colors ${
                      selectedTemplate === template.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    disabled={loading}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              
              {/* Rich Text Toolbar */}
              <div className="border border-gray-300 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={formatBold}
                  disabled={loading}
                  className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={formatItalic}
                  disabled={loading}
                  className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={formatUnderline}
                  disabled={loading}
                  className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  title="Underline"
                >
                  <Underline className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={formatHeading}
                  disabled={loading}
                  className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  title="Heading"
                >
                  <Type className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={insertList}
                  disabled={loading}
                  className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  title="List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={insertLink}
                  disabled={loading}
                  className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  title="Link"
                >
                  <Link2 className="w-4 h-4" />
                </button>
              </div>
              
              {/* Message Textarea */}
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                placeholder="Compose your message here..."
                className="w-full border border-gray-300 border-t-0 rounded-b-md p-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows="12"
                disabled={loading}
              />
              
              <p className="text-xs text-gray-500 mt-2">
                Use the toolbar above to format your text. A default footer with company information will be automatically added to your email.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={handleClear}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            disabled={loading}
          >
            Clear
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSendEmail}
              disabled={loading || !subject.trim() || !message.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposerModal;